import React, { useState, useEffect } from 'react';
import { requestAndSubscribe, getSubscriptionStatus } from '../services/pushClient';

// ─── Notification permission prompt (v85b) ──────────────────────────────
// Shown once, the first time a logged-in user is running Greentime in
// installed-PWA mode (display-mode: standalone). NOT shown in regular
// Safari/Chrome — push doesn't work there on iOS, and Android browser
// push without install has worse UX.
//
// Stored dismissal: localStorage key 'gt_push_dismissed_v1' = 'true'.
// Mirror of the InstallPrompt pattern for consistency.

const DISMISS_KEY = 'gt_push_dismissed_v1';

const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

export default function NotificationPermissionPrompt() {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Hard gates: only show in PWA standalone mode, only once.
      if (!isStandalone()) return;
      if (localStorage.getItem(DISMISS_KEY) === 'true') return;

      // Don't re-ask if the browser already has a decision (granted or
      // denied). 'default' is the only state where the prompt makes sense.
      const status = await getSubscriptionStatus();
      if (!status.supported) return;
      if (status.permission !== 'default') return;
      if (status.subscribed) return; // edge case: subscribed without our dismiss flag

      // Slight delay so we don't slap the user the instant they launch.
      setTimeout(() => { if (!cancelled) setVisible(true); }, 1500);
    })();
    return () => { cancelled = true; };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, 'true');
    setVisible(false);
  };

  // CRITICAL: this runs in a direct click-handler so iOS Safari treats the
  // permission request as user-initiated. Don't add setState→rerender
  // indirection before the requestPermission call inside requestAndSubscribe.
  const enable = async () => {
    setBusy(true); setError('');
    const result = await requestAndSubscribe();
    setBusy(false);
    if (result.ok) {
      localStorage.setItem(DISMISS_KEY, 'true');
      setVisible(false);
    } else {
      setError(result.reason || 'Could not enable notifications.');
    }
  };

  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed',
      // v85b.1: respect iOS home-bar safe area (16px gap PLUS the inset).
      bottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
      left: 16, right: 16,
      maxWidth: 420, margin: '0 auto', zIndex: 1000,
      background: '#fff', border: '1px solid #e8e4d8',
      borderRadius: 12, padding: 16,
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
      fontFamily: "'DM Sans', Arial, sans-serif",
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <img
          src="/apple-touch-icon.png"
          alt=""
          width={44}
          height={44}
          style={{ borderRadius: 10, flexShrink: 0 }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 500, color: '#0d2b1a', marginBottom: 4 }}>
            Get notified about tee times
          </div>
          <div style={{ fontSize: 12.5, color: '#5a5a4a', lineHeight: 1.45 }}>
            We'll ping you when someone joins, cancels, or a spot opens — same
            categories as your email preferences.
          </div>
          {error && (
            <div style={{ fontSize:12, color:'#c33', marginTop:8, lineHeight:1.4 }}>{error}</div>
          )}
          <div style={{ marginTop: 10, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              onClick={dismiss}
              disabled={busy}
              style={{
                background: 'transparent', border: 'none', color: '#7a7a6a',
                fontSize: 13, padding: '6px 10px', cursor: 'pointer',
              }}
            >
              Not now
            </button>
            <button
              onClick={enable}
              disabled={busy}
              style={{
                background: '#7ec99a', color: '#0d2b1a', border: 'none',
                borderRadius: 8, padding: '8px 14px', fontSize: 13,
                fontWeight: 500, cursor: busy ? 'not-allowed' : 'pointer',
                opacity: busy ? 0.6 : 1,
              }}
            >
              {busy ? 'Enabling…' : 'Yes, notify me'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
